import Dialogue from 'components/Dialogue';
import type { DialogueProps } from 'components/Dialogue/types';
import { ScenarioProvider } from '../MessageScenario/ScenarioContext';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchMsgSelectedSendTime } from 'hooks/business/searchMsgSelectedMsg';
import useChatView from '../../useChatView';
import { useDebounceEffect, useMemoizedFn, useSafeState } from 'ahooks';
import { isNil, last } from 'lodash-es';
import { HistoryDivider } from '../HistoryDivider';
import { QuestionCircleOutlined, RightOutlined, SyncOutlined, UpOutlined } from '@ant-design/icons';
import useInfiniteLoad from 'hooks/useInfiniteLoad';
import useTrackingRef from 'hooks/useTrackingRef';
import { getFailContent } from 'hooks/business/im/useMsgToConversation';
import useCancelToken from 'hooks/useCancelToken';
import posthog from '@leyan/lytics';
import { useChatContext } from '../../contexts/ChatContext';
import { isString } from 'lodash-es';
import useMessageRender from './useMessageRender';
import { Anchor } from 'components/Dialogue/Anchor';
import { Divider, Tooltip } from 'antd';
import { eventBus } from 'services/eventBus';
import { useScrollToMsg } from 'hooks/business/im/useScrollToMsg';
import { useAgentPageReader } from 'views/Agent/page-context';
import { conversationAgentContext } from '../../agent-context';
import type { DialogueRefObject } from 'components/Dialogue/types';

export interface ChatDialogueProps {
  // 是否只渲染消息列表
  isGalaxy?: boolean;
  conversation: Conversation.Conversation;
  iframeMode?: boolean;
  galaxyModule?: Conversation.GalaxyModule;
  filterMessages?: string[];
  preventPosScroll?: boolean;
  // 与美客多对话窗口
  onMercadoChatView?: boolean;
  claimId?: string;
  highlightMsg?: Record<string, string>;
}

type UpdateMessgeScrollTo = DialogueProps['pos'] | 'auto' | 'prevent';

type DataOffset = string | false | undefined;

export const ChatDialogue = (props: ChatDialogueProps) => {
  const agentDialogueRef = useRef<DialogueRefObject | null>(null);
  const {
    isGalaxy,
    conversation,
    iframeMode,
    filterMessages,
    preventPosScroll,
    onMercadoChatView,
    claimId,
  } = props;

  const { dialogRef, setDialogRef, highlightMsg: contextHighlightMsg } = useChatContext();
  const { conversationId, unreadCount } = conversation;
  const { keyMsgId, messages, fetch, nouseMsgList } = useChatView(
    iframeMode,
    onMercadoChatView,
    undefined,
    !isGalaxy,
    claimId,
  );
  const { t, messageRender, nouseExpandMap, setNouseExpandMap } = useMessageRender(
    conversation,
    nouseMsgList,
  );

  const scrollToMsg = useScrollToMsg();

  const anchorExtra = Object.values(nouseExpandMap).some((_) => _) ? (
    <Anchor onClick={() => setNouseExpandMap({})}>
      <Tooltip
        title={
          <div onClick={(e) => e.stopPropagation()}>
            {t('scene.chatWorkspace.messageThread.divider.foldedMessages')}
            <span
              className="cursor-pointer ml-2"
              onClick={() => {
                eventBus.emit('SHOW_SYSTEM_MODAL', {
                  activeKey: 'preference',
                  activeFeature: 'simpleMode',
                });
              }}
            >
              {t('scene.chatWorkspace.messageThread.action.goToSettings')}
              <RightOutlined />
            </span>
          </div>
        }
      >
        <QuestionCircleOutlined style={{ color: 'var(--fett-text-tertiary)' }} />
      </Tooltip>
      <Divider type="vertical" style={{ marginRight: '2px', marginLeft: '6px' }} />
      <span>{t('scene.chatWorkspace.messageThread.action.collapse')}</span>
      <UpOutlined />
    </Anchor>
  ) : null;

  const { isCancel, newCancelToken } = useCancelToken();
  const searchSelectedMsg = useSearchMsgSelectedSendTime();
  const searchScrolledRef = useRef(false);

  const [dataOffset, setDataOffset] = useSafeState<DataOffset>(false);
  const [updating, setUpdating] = useSafeState(false);
  const [fetchError, setFetchError] = useSafeState(false);
  /**
   * 通过搜索跳转时，会直接跳转到指定的消息，此时向下查找会有断层，
   * 为了界面稳定，记录中断的magId，并进行slice
   */
  const [interruptMsgId, setInterruptMsgId] = useSafeState<Conversation.Message['msgId']>('');
  const [toLatestOffset, setToLatestOffset] = useSafeState<DataOffset>(undefined);
  const [latestHasMore, setLatestHasMore] = useSafeState<boolean>(false);
  const [localHighlightMsg, setLocalHighlightMsg] = useSafeState<Record<string, boolean>>({});

  const updatingRef = useTrackingRef(updating);
  const currentConversationIdRef = useTrackingRef(conversationId);
  const searchSelectedMsgRef = useTrackingRef(searchSelectedMsg);
  const conversationRef = useTrackingRef(conversation);

  const readedMessageIdRef = useRef('-1');

  if (fetchError) {
    throw new Error(
      JSON.stringify({
        conversationId,
        error: '消息列表加载失败',
      }),
    );
  }

  /**
   * @params dir: 0：往小于sendTime的方向,向上翻页; 1：往大于sendTime的方向,向下翻页，默认0
   * @params scrollTo: 'auto'： 自动依据dir进行跳转, default: 'auto'
   * @params offestParam: 强制指定需要使用的offset
   */
  const updateMessage = useMemoizedFn(
    async (
      dir = 0,
      scrollTo: UpdateMessgeScrollTo = 'auto',
      offestParam?: DataOffset,
      pageSize?: number,
    ) => {
      if (updatingRef.current) {
        return false;
      }
      setUpdating(true);
      const PAGE_SIZE = pageSize || (filterMessages && filterMessages.length > 0 ? 9999 : 50);

      const oldestMessage = messages[0];
      readedMessageIdRef.current = oldestMessage?.dialogueUuid || '-1';
      let offset: string | undefined;
      const isFindingPrev = dir === 0;

      if (!isNil(offestParam)) {
        offset = offestParam || undefined;
      } else if (isFindingPrev) {
        offset = oldestMessage?.time ? `${oldestMessage?.time}` : dataOffset || undefined;
      } else {
        offset = toLatestOffset || '';
      }
      const rsp = await fetch(
        {
          page_size: PAGE_SIZE,
          offset,
          dir,
          with_mercado: onMercadoChatView,
          claimId,
        },
        newCancelToken,
      )
        .then(async (data) => {
          // 为免外部没有正常keyed导致的错误，
          // 对比更新后的conversationid与闭包中(请求发起时的conversationId)
          // 防止不同conversationId之间的异步行为导致界面逻辑的错误响应
          if (currentConversationIdRef.current !== conversationId) {
            return data;
          }
          setFetchError(false);
          if (!Array.isArray(data) || data.length === 0) {
            // 空结果说明该方向没有更多消息，标记结束，避免加载哨兵反复触发空请求
            if (isFindingPrev) {
              setDataOffset(false);
            } else {
              setLatestHasMore(false);
            }
            return false;
          }

          let target: DialogueProps['pos'] = isString(scrollTo) ? undefined : scrollTo;
          if (searchSelectedMsgRef.current?.msgId) {
            target = { uuid: searchSelectedMsgRef.current.msgId };
            searchSelectedMsgRef.current = {
              msgId: '',
              sendTime: '',
            };
          }
          if (scrollTo === 'auto') {
            if (isFindingPrev) {
              target = oldestMessage?.dialogueUuid
                ? { uuid: oldestMessage?.dialogueUuid }
                : 'bottom';
            } else {
              const latestMessage = last(messages);
              target = latestMessage?.dialogueUuid
                ? { uuid: latestMessage.dialogueUuid, options: { inline: 'nearest', block: 'end' } }
                : 'bottom';
            }
          }
          if (typeof target === 'object') {
            if (target?.uuid) {
              dialogRef?.scrollToMessage(target.uuid, target?.options);
            }
          } else if (!isNil(target)) {
            dialogRef?.scrollTo(target);
          }
          let interruptId = '';
          const hasMore = data.length >= PAGE_SIZE;
          let latestData = last(data)!;
          if (!isFindingPrev) {
            const isContinuous = messages.some((msg) => msg?.dialogueUuid === latestData?.msgId);
            if (isContinuous) {
              const currentLastMsg = last(messages)!.raw;
              if (currentLastMsg.msgId !== latestData.msgId) {
                latestData = currentLastMsg;
              }
            } else if (!isContinuous && !isFindingPrev) {
              interruptId = latestData.msgId;
            }
            setToLatestOffset(latestData.sendTime as string);
            setLatestHasMore(hasMore);
            setInterruptMsgId(hasMore ? interruptId : '');
          } else {
            setDataOffset(hasMore ? (latestData.sendTime as string) : false);
          }

          return data;
        })
        .catch((e) => {
          if (isCancel(e)) return;

          posthog.capture('$loadMessageFailed', {
            conversation: conversationRef.current,
          });

          setFetchError(true);
        });
      setUpdating(false);
      return rsp;
    },
  );

  useEffect(() => {
    const handler = () => updateMessage();

    eventBus.on('LOAD_MORE_MESSAGE', handler);

    return () => eventBus.off('LOAD_MORE_MESSAGE', handler);
  }, [updateMessage]);

  const { ref: infinitLoadRef, status } = useInfiniteLoad<HTMLDivElement>(async () => {
    let target: UpdateMessgeScrollTo = 'auto';
    let offset: DataOffset = dataOffset;
    let dir: 1 | 0 = 0;
    const withSearchMsgId = searchSelectedMsgRef.current?.msgId;
    if (withSearchMsgId && !searchScrolledRef.current) {
      return Promise.resolve();
    } else {
      const failMsgs = messages.filter((x) => x && getFailContent(x.raw));
      if (unreadCount && failMsgs.length) {
        const failMsgs = messages.filter((x) => x && getFailContent(x.raw));
        target = { uuid: failMsgs?.reverse()?.[0]?.raw.msgId ?? 'auto' };
      }
    }
    await updateMessage(dir, target, offset);
  }, dataOffset);

  const inifiniteTips = useMemo(() => {
    const noMore = status === 'no_more';
    return (
      <div className="text-xs text-center" ref={infinitLoadRef}>
        {noMore ? (
          <HistoryDivider>
            {t('scene.chatWorkspace.messageThread.divider.noMoreMessages')}
          </HistoryDivider>
        ) : (
          <HistoryDivider>
            {t('scene.chatWorkspace.messageThread.divider.historyStart')} <SyncOutlined spin />
          </HistoryDivider>
        )}
      </div>
    );
  }, [infinitLoadRef, status, t]);

  const { ref: infinitLoadRefBottom } = useInfiniteLoad<HTMLDivElement>(
    async () => {
      const target: UpdateMessgeScrollTo = interruptMsgId
        ? { uuid: interruptMsgId, options: { block: 'end', inline: 'nearest' } }
        : 'auto';

      await updateMessage(1, target);
    },
    updating ? false : toLatestOffset,
    {
      root: dialogRef?.dom || null,
      rootMargin: `0% 0% 30px 0%`,
      threshold: 1,
    },
  );

  const inifiniteTipsBottom = useMemo(() => {
    return (
      <div
        className="text-xs text-center"
        ref={infinitLoadRefBottom}
        style={{ display: `${latestHasMore ? 'block' : 'none'}` }}
      >
        <HistoryDivider>
          {t('scene.chatWorkspace.messageThread.divider.newerMessages')} <SyncOutlined spin />
        </HistoryDivider>
      </div>
    );
  }, [latestHasMore, t, infinitLoadRefBottom]);

  const dialoguePost = useMemo(() => {
    return searchSelectedMsg?.msgId ? { uuid: searchSelectedMsg?.msgId } : 'bottom';
  }, [searchSelectedMsg?.msgId]);

  const showMessages = useMemo(() => {
    if (interruptMsgId) {
      const idx = messages.findIndex((msg) => msg && msg.raw.msgId === interruptMsgId);
      if (idx >= 0) {
        return messages.slice(0, idx);
      }
    }
    if (filterMessages && filterMessages.length > 0) {
      return messages.filter((msg) => msg && filterMessages.includes(msg.raw.msgId));
    }
    return messages;
  }, [messages, interruptMsgId, filterMessages]);

  useDebounceEffect(
    () => {
      if (searchSelectedMsgRef.current?.msgId) {
        const offset = `${searchSelectedMsg.sendTime}`;
        const pageSize = isGalaxy ? 5 : 50;
        fetch(
          [
            {
              page_size: pageSize,
              dir: 1,
              offset,
              with_mercado: onMercadoChatView,
              claimId,
            },
            {
              page_size: pageSize,
              dir: 0,
              offset,
              with_mercado: onMercadoChatView,
              claimId,
            },
          ],
          newCancelToken,
        ).then((data) => {
          if (!data.length) {
            return;
          }
          const times = data.map(({ sendTime }) => Number(sendTime));
          const min = Math.min(...times).toString();
          const max = Math.max(...times).toString();
          setTimeout(() => {
            if (dialogRef) {
              dialogRef.scrollToMessage(searchSelectedMsgRef.current.msgId, {
                behavior: 'smooth',
                block: searchSelectedMsgRef.current.block || 'center',
                inline: 'nearest',
              });
              searchScrolledRef.current = true;
            }
          });
          setTimeout(() => {
            setDataOffset(min);
            // 初始化向下翻页状态，否则搜索跳转后无法向下加载更新的消息
            setToLatestOffset(max);
            setLatestHasMore(data.length >= pageSize);
            // 清理上一次跳转遗留的断层标记
            setInterruptMsgId('');
          }, 500);
        });
      } else {
        // 切换会话时重置向下翻页状态，避免沿用上一个会话搜索跳转留下的断层状态
        setToLatestOffset(undefined);
        setLatestHasMore(false);
        setInterruptMsgId('');

        // 主动获取当前最后的数据
        // 获取首屏数据时offset应该为空 @丁青松
        updateMessage(0, 'bottom', '');

        // 通过搜索跳转时可能导致messsages数组存在非最新数据的断层，暂时不使用这种方式
        // setDataOffset(messages[0]?.time ? `${messages[0]?.time}` : '');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [conversationId, searchSelectedMsg],
    { wait: 100 },
  );

  const flashHighlightMsgTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const flashHighlightMsg = useCallback(
    (msgId: string) => {
      // 清除之前的定时器（如果存在）
      const existingTimeout = flashHighlightMsgTimeoutRef.current.get(msgId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // 设置高亮状态
      setLocalHighlightMsg((prev) => ({
        ...prev,
        [msgId]: true,
      }));

      // 设置定时器来移除高亮
      const timeoutId = setTimeout(() => {
        setLocalHighlightMsg((prev) => {
          const newState = { ...prev };
          delete newState[msgId];
          return newState;
        });
        flashHighlightMsgTimeoutRef.current.delete(msgId);
      }, 4000);

      // 存储定时器 ID 以便清理
      flashHighlightMsgTimeoutRef.current.set(msgId, timeoutId);
    },
    [setLocalHighlightMsg],
  );

  useEffect(() => {
    let isMounted = true;
    const scrollTimeoutIds: NodeJS.Timeout[] = [];
    // 在 effect 内部保存 ref 的引用，避免清理函数中使用过时的 ref
    const timeoutRef = flashHighlightMsgTimeoutRef;

    const handler = (msgId: string) => {
      if (!isMounted) return;

      const existedMsg = showMessages.find(
        (msg) => msg && msg.raw.msgId.toString() === msgId.toString(),
      );
      if (existedMsg) {
        dialogRef?.scrollToMessageByScrollTo(
          msgId,
          {
            behavior: 'smooth',
          },
          true,
        );
        requestAnimationFrame(() => {
          if (isMounted) {
            flashHighlightMsg(msgId);
          }
        });
      } else {
        scrollToMsg(msgId).then(() => {
          if (!isMounted) return;
          const timeoutId = setTimeout(() => {
            if (isMounted) {
              flashHighlightMsg(msgId);
            }
          }, 1000);
          scrollTimeoutIds.push(timeoutId);
        });
      }
    };

    eventBus.on('SCROLL_TO_MSG_BY_ID', handler);

    return () => {
      isMounted = false;
      eventBus.off('SCROLL_TO_MSG_BY_ID', handler);
      // 清理所有未完成的定时器
      scrollTimeoutIds.forEach((id) => clearTimeout(id));
      // 清理所有 flashHighlightMsg 的定时器
      if (timeoutRef.current) {
        timeoutRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
        timeoutRef.current.clear();
      }
    };
  }, [dialogRef, flashHighlightMsg, scrollToMsg, showMessages]);

  // 合并本地高亮状态和上下文高亮状态
  const mergedHighlightMsg = useMemo(() => {
    if (!contextHighlightMsg) {
      return Object.entries(localHighlightMsg).reduce((acc, [key, value]) => {
        acc[key] = value ? 'flash-animation' : '';
        return acc;
      }, {} as Record<string, string>);
    }
    // 如果 contextHighlightMsg 的 key 在 localHighlightMsg 能查到，则 contextHighlightMsg 的对应值增加 ' flash- animation'，并返回新的 contextHighlightMsg
    const newHighlightMsg: Record<string, string> = {};
    Object.entries(contextHighlightMsg).forEach(([key, value]) => {
      if (localHighlightMsg[key]) {
        // 增加 flash- animation
        newHighlightMsg[key] = value + ' flash-animation';
      } else {
        newHighlightMsg[key] = value;
      }
    });
    return newHighlightMsg;
  }, [contextHighlightMsg, localHighlightMsg]);

  useAgentPageReader(() => {
    const element = agentDialogueRef.current?.dom;
    if (
      isGalaxy ||
      iframeMode ||
      !element?.getClientRects().length ||
      getComputedStyle(element).visibility === 'hidden'
    )
      return undefined;
    return {
      conversation: conversationAgentContext(
        conversation,
        showMessages.map((message) => message.raw),
        onMercadoChatView ? 'mercado' : 'buyer',
      ),
    };
  });

  return (
    <ScenarioProvider>
      <Dialogue
        isGalaxy={isGalaxy}
        className="flex-grow flex-shrink-0 basis-0"
        pos={dialoguePost}
        key={conversationId}
        promoteHumanMsgId={keyMsgId}
        header={inifiniteTips}
        footer={inifiniteTipsBottom}
        anchorExtra={anchorExtra}
        ref={(dialogueRef) => {
          agentDialogueRef.current = dialogueRef;
          setDialogRef(dialogueRef);
        }}
        conversation={conversation}
        messageRender={messageRender}
        messages={showMessages}
        preventPosScroll={preventPosScroll}
        highlightMsg={mergedHighlightMsg}
      />
    </ScenarioProvider>
  );
};
